import { Component, Input, OnInit } from '@angular/core';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { OptionsStoreService, TableOptionsState, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';
import { UserOverview } from '@core/core.state';
import { compare, compareDate } from '@core/customer-space-admin/utils/cs-user.utils';
import { UserOverviewDto } from '@core/users/user.model';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { RootState } from '@root/app.state';
import { go } from '@platform/router.actions';
import { PageEvent } from '@angular/material/paginator';
import { defaultPageSize } from '@platform/shared/utilities/paginator-utility';
import { UserType } from '@platform/components/user-is-external-dropdown/user-is-external-dropdown.component';

@Component({
  selector: 'app-users-management-table',
  templateUrl: './users-management-table.component.html',
  styleUrls: ['./users-management-table.component.scss'],
})
export class UsersManagementTableComponent implements OnInit {
  @Input() users: UserOverview[] = [];
  dataSource: MatTableDataSource<UserOverview> = new MatTableDataSource<UserOverview>();
  sortOptions: Sort; // options from local storage

  selectedUserType: UserType = null;
  searchTerm: string;
  tableOptions: TableOptionsState;

  constructor(
    private _optionsStoreService: OptionsStoreService,
    private translateService: TranslateService,
    private store$: Store<RootState>,
  ) {
    // set from local storage
    this.tableOptions = this._optionsStoreService.getOptionsOrDefault(UiTableKeys.PortalUsers);
    this.sortOptions = this.tableOptions.sort;
    this.selectedUserType = this.tableOptions.userTypeFilter as UserType;

    this.dataSource = new MatTableDataSource<UserOverview>([]);
    this.dataSource.sortData = this.sortData2;
    this.dataSource.filterPredicate = this.filterPredicate();
    this.applyFilter();
  }

  // sort options from localStorage
  get initialSortActive(): string {
    return this.sortOptions.active;
  }

  get initialSortDirection(): SortDirection {
    return this.sortOptions.direction;
  }

  get initialPage(): number {
    return this.tableOptions.pageIndex || 0;
  }

  get initialPageSize(): number {
    return this.tableOptions.pageSize || defaultPageSize;
  }

  ngOnInit(): void {
    this.dataSource.data = this.users;
  }

  navigateToUserProfile(user: UserOverviewDto, $event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.store$.dispatch(
      go({
        path: ['/', 'users', user.id],
      }),
    );
  }

  getAccountType(isInternal: boolean): string {
    return isInternal ? 'Internal' : 'External';
  }

  getIsActiveValueTranslationKey(isActive: boolean): string {
    return isActive ? 'shared.yes' : 'shared.no';
  }

  /** Announce the change in sort state for localstorage */
  storeSortOptions(sortState: Sort) {
    this._optionsStoreService.patchOptions(UiTableKeys.PortalUsers, { sort: sortState });
    this.sortOptions = sortState;
  }

  storePageOptions(event: PageEvent) {
    this._optionsStoreService.patchOptions(UiTableKeys.PortalUsers, { pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onUserTypeChange(event: UserType) {
    this._optionsStoreService.patchOptions(UiTableKeys.PortalUsers, { userTypeFilter: event });
    this.selectedUserType = event;
    this.applyFilter();
  }

  // This is the method which get called from your filter input
  onSearchChange(filterValue: string) {
    filterValue = filterValue || '';
    this.searchTerm = filterValue.trim().toLowerCase();
    this.applyFilter();
  }

  applyFilter() {
    const params = new URLSearchParams();
    params.set('s', this.searchTerm || '');
    params.set('ut', this.selectedUserType || '');
    this.dataSource.filter = params.toString();
  }

  // custom filter function
  filterPredicate() {
    const filterFunction = (data: UserOverview, filter: string): boolean => {
      const queryParams = new URLSearchParams(filter);
      const searchTerm = queryParams.get('s') || '';
      const userType = queryParams.get('ut');

      const isInternal = data.isInternal;

      if (userType === 'internal' && !isInternal) {
        return false;
      }

      if (userType === 'external' && isInternal) {
        return false;
      }

      if (searchTerm === 'active' || searchTerm === 'activ') {
        return data.isActive;
      }
      if (searchTerm === 'inactive' || searchTerm === 'inactiv') {
        return !data.isActive;
      }
      const concatFields = concatCustomerUserFields(data, this.translateService);
      return concatFields.indexOf(searchTerm) !== -1;
    };

    return filterFunction;
  }

  sortData2(items: UserOverview[], sort: MatSort): UserOverview[] {
    if (!sort.active || sort.direction === '') {
      return items;
    }

    const isAsc = sort?.direction === 'asc';

    return items.sort((a: UserOverview, b: UserOverview) => {
      switch (sort?.active) {
        case 'firstName':
          return compare(a.firstName, b.firstName, isAsc);
        case 'lastName':
          return compare(a.lastName, b.lastName, isAsc);
        case 'account':
          return compare(a.isInternal ? 'internal' : 'external', b.isInternal ? 'internal' : 'external', isAsc);
        case 'organization':
          return compare(a.organizationUnit, b.organizationUnit, isAsc);
        case 'email':
          return compare(a.mail, b.mail, isAsc);
        case 'createdDate':
          return compareDate(a.accountCreatedDate, b.accountCreatedDate, isAsc);
        case 'lastLogin':
          return compareDate(a.lastSuccessfulLoginDate, b.lastSuccessfulLoginDate, isAsc);
        case 'isActive':
          return compare(a.isActive ? 'active' : 'inactive', b.isActive ? 'active' : 'inactive', isAsc);
        default:
          return 0;
      }
    });
  }
}

function concatCustomerUserFields(data: UserOverview, tr: TranslateService): string {
  const activeT = tr.instant('users.active');
  const inactiveT = tr.instant('users.inactive');
  const arr: string[] = [
    data.firstName,
    data.lastName,
    data.organizationUnit,
    data.mail,
    data.mobileNumber,
    data.isActive ? activeT : inactiveT,
    data.isInternal ? 'Internal' : 'External',
    data.id,
  ];

  return arr
    .filter((item) => item != null)
    .map((item) => item.toLowerCase())
    .join(',');
}
